import { useCustomFieldDefinitions } from "@/hooks/use-custom-fields";
import { useCatalogCategories, useDesks } from "@/hooks/use-desk-migration";
import type { MondayHarmonyField } from "@/lib/types/itsm-config";

const BUILTIN_LABELS: Record<string, string> = {
  title: "Title",
  description: "Description",
  priority: "Priority",
  assignee: "Assignee",
  reporter: "Reporter",
  status: "Status",
};

/**
 * The tenant's desk custom fields and catalog items, and a label for any
 * Harmony field a field mapping names - shared by the monday and Jira
 * field-mapping editors.
 */
export function useHarmonyFieldLabels(tenantId: string, deskIds: string[]) {
  const fields = useCustomFieldDefinitions(tenantId, deskIds);
  const { data: desks } = useDesks(tenantId);
  const { data: catalog, isLoading: catalogLoading } =
    useCatalogCategories(tenantId);
  const items = (catalog?.categories ?? []).flatMap((c) => c.items ?? []);

  const customLabel = (id: string) => {
    const def =
      fields.definitions.find((d) => d.id === id) ??
      fields.legacyDefinitions.find((d) => String(d.id) === id);
    if (!def) return `Deleted field #${id}`;
    const desk = def.desk_id
      ? (desks?.find((d) => d.id === def.desk_id)?.name ?? def.desk_id)
      : "all desks";
    return `${def.name} · ${desk}`;
  };
  const label = (field: MondayHarmonyField) => {
    if (field.kind === "builtin") return BUILTIN_LABELS[field.key] ?? field.key;
    if (field.kind === "custom") return `Custom: ${customLabel(field.key)}`;
    const item = items.find((i) => i.id === field.catalog_item_id);
    return `Catalog: ${field.key} · ${item?.name ?? field.catalog_item_id}`;
  };
  return { fields, items, catalogLoading, label };
}
